import {_utils, viewportChecker} from "../../utils/_utils.js";
import {_libs} from "../../utils/_libs.js";
import {_state} from "../../state/state.js";
import {watchPageHeight} from "../../utils/scroll-refresh.js";
import {onSwipe} from "../../utils/swipe.js";

/**
 * About — "Our story in milestones" (TZ section 9).
 * - Desktop: the body is position: sticky inside a taller stage (CSS, 1 screen per milestone); scroll progress
 *   picks the active milestone → year indicator moves down, text + photo switch; scroll up plays it back.
 *   Clicking a year scrolls to its slice of the stage.
 * - Mobile/tablet: simple slider — tap a year or swipe anywhere on the block (no scroll effect).
 * Rebuilt only when crossing the desktop breakpoint (resizePage fires on any page-height change).
 */
export const abMilestones = () => {
    return new Promise((resolve) => {
        _utils.initSection('.ab-milestones', (section) => init(section));
        watchPageHeight();
        resolve();
    });
};

const init = (section) => {
    const stage = section.querySelector('.ab-milestones__stage');
    const sticky = section.querySelector('.ab-milestones__sticky');
    const years = [...section.querySelectorAll('.ab-milestones__year')];
    const items = [...section.querySelectorAll('.ab-milestones__item')];
    const images = [...section.querySelectorAll('.ab-milestones__image')];
    if (!stage || !sticky || years.length < 2) return;

    const count = years.length;
    let current = 0;
    let tl = null;

    const setActive = (index) => {
        index = Math.max(0, Math.min(count - 1, index));
        if (index === current) return;
        current = index;

        section.style.setProperty('--ms-active', String(index));
        years.forEach((year, i) => {
            year.classList.toggle('is-active', i === index);
            year.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
        items.forEach((item, i) => {
            item.classList.toggle('is-active', i === index);
            item.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        });
        images.forEach((image, i) => image.classList.toggle('is-active', i === index));
    };

    const stickyTop = () => parseFloat(getComputedStyle(sticky).top) || 0;

    const createTl = () => {
        if (tl !== null) {
            tl.scrollTrigger && tl.scrollTrigger.kill(true);
            tl.kill();
            tl = null;
        }

        if (_state.isDesktop) {
            // from the moment the body sticks until it un-sticks (stage bottom = sticky bottom)
            tl = _libs._gsap({
                trigger: stage,
                start: () => `top ${stickyTop()}px`,
                end: () => `bottom ${stickyTop() + sticky.offsetHeight}px`,
                invalidateOnRefresh: true,
                //markers: true,
                onUpdate: (self) => {
                    setActive(Math.min(count - 1, Math.floor(self.progress * count)));
                },
            });
        }
    };

    createTl();

    // years: desktop → scroll to the milestone's slice; mobile → switch directly
    years.forEach((year, index) => {
        year.addEventListener('click', () => {
            if (_state.isDesktop && tl?.scrollTrigger) {
                const st = tl.scrollTrigger;
                const target = st.start + (st.end - st.start) * ((index + 0.5) / count);
                if (_state.lenisInit !== null) {
                    _state.lenisInit.scrollTo(target, {duration: 1});
                } else {
                    window.scrollTo({top: target, behavior: 'smooth'});
                }
            } else {
                setActive(index);
            }
        });
    });

    // mobile/tablet: swipe anywhere on the block (touch or mouse drag)
    onSwipe(section.querySelector('.ab-milestones__body'), (dir) => setActive(current + dir), () => !_state.isDesktop);

    let wasDesktop = _state.isDesktop;
    _utils.rebuildComponentsOnResize(() => {
        viewportChecker.all(); // updates _state.isDesktop
        if (_state.isDesktop === wasDesktop) return;
        wasDesktop = _state.isDesktop;
        createTl();
    });
};
